/**
 * @license
 * Copyright Endlessjs. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import { InjectionToken } from '@angular/core';

import { ElGlobalLogicalPosition, ElGlobalPosition } from '../cdk/overlay/position-helper';
import { ElComponentStatus } from '../component-status';
import { ElIconConfig } from '../icon/icon.component';

type IconToClassMap = {
  [status in ElComponentStatus]: string;
}

export const EL_TOASTR_CONFIG = new InjectionToken<ElToastrConfig>('Default toastr options');

export type ElDuplicateToastBehaviour = 'previous' | 'all';

/**
 * The `ElToastrConfig` class describes configuration of the `ElToastrService.show` and global toastr configuration.
 * */
export class ElToastrConfig {
  /**
   * Determines where on the screen toast have to be rendered.
   * */
  position: ElGlobalPosition = ElGlobalLogicalPosition.TOP_END;
  /**
   * Status chooses color scheme for the toast.
   * */
  status: '' | ElComponentStatus = 'primary';
  /**
   * Duration is timeout between toast appears and disappears.
   * */
  duration: number = 3000;
  /**
   * Destroy by click means you can hide the toast by clicking it.
   * */
  destroyByClick: boolean = true;
  /**
   * If preventDuplicates is true then the toast with the same title, message and status will not be rendered.
   * Find duplicates behaviour determined by `preventDuplicates`.
   * The default `previous` duplicate behaviour is used.
   * */
  preventDuplicates: boolean = false;
  /**
   * Determines the how to threat duplicates.
   * */
  duplicatesBehaviour: ElDuplicateToastBehaviour = 'previous';
  /*
  * The number of visible toasts. If the limit exceeded the oldest toast will be removed.
  * */
  limit?: number = null;
  /**
   * Determines render icon or not.
   * */
  hasIcon: boolean = true;
  /**
   * Icon name or icon config object that can be provided to render custom icon.
   * */
  icon: string | ElIconConfig = 'email';
  /**
   * Icon pack to look for the icon in.
   * @deprecated Set pack via icon config object passed to icon property
   * @breaking-change 5.0.0
   * */
  iconPack: string;
  /**
   * Toast status icon-class mapping.
   * */
  protected icons: IconToClassMap = {
    danger: 'flash-outline',
    success: 'checkmark-outline',
    info: 'question-mark-outline',
    warning: 'alert-triangle-outline',
    primary: 'email-outline',
  };

  constructor(config: Partial<ElToastrConfig>) {
    this.patchIcon(config);
    Object.assign(this, config);
  }

  protected patchIcon(config: Partial<ElToastrConfig>) {
    if (!('icon' in config)) {
      config.icon = {
        icon: this.icons[config.status || 'primary'],
        pack: 'endlessjs-essentials',
      };
    }
  }
}
